import type { CharacterPermanent } from '@danda/shared';

interface CharacterCardProps {
  character: CharacterPermanent;
  onClick?: () => void;
}

export default function CharacterCard({ character, onClick }: CharacterCardProps) {
  const level = character.level ?? 1;
  const bag = character as Record<string, unknown>;
  const avatarUrl = (bag.avatarUrl ?? bag.avatar_url) as string | undefined;
  const portraitUrl = (bag.portraitUrl ?? bag.portrait_url) as string | undefined;
  const imageUrl = avatarUrl || portraitUrl;

  return (
    <div
      className={`rounded-xl border bg-card/40 text-card-foreground shadow-lg backdrop-blur-md p-6${onClick ? ' cursor-pointer hover:bg-card/50 transition-colors' : ''}`}
      onClick={onClick}
    >
      <div className="flex items-start gap-4">
        {/* Avatar */}
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={character.name}
            className="flex-shrink-0 w-16 h-16 rounded-full object-cover"
          />
        ) : (
          <div className="flex-shrink-0 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center text-3xl">
            {character.avatar_emoji || '⚔️'}
          </div>
        )}

        {/* Info */}
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-bold text-foreground truncate font-character">
            {character.name}
          </h3>
          <p className="text-sm text-muted-foreground">
            Level {level} {character.species} {character.class}
          </p>
          {character.status && character.status !== 'active' && (
            <span className="inline-block mt-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-800 text-gray-200">
              {character.status.charAt(0).toUpperCase() + character.status.slice(1)}
            </span>
          )}
        </div>
      </div>

      {/* Portrait — full width below if available */}
      {portraitUrl && (
        <div className="mt-4 -mx-2">
          <img src={portraitUrl} alt={`${character.name} portrait`} className="w-full rounded-lg" />
        </div>
      )}
    </div>
  );
}
